<div fxLayout>
  <sbnb-subnav fxFlex="280px" fxHide.lt-md class="subnav__semi-reduced-padding">
    <sbnb-back-button style="margin-bottom: 20px"></sbnb-back-button>
    <sbnb-helptext-wrapper [area]="'sidebar'" [pageId]="'gx-inbox-ai-settings'"></sbnb-helptext-wrapper>
  </sbnb-subnav>

  <main fxFlex class="subnav__exists" [ngClass.lt-md]="{ mobile: true, subnav__exists: false }">
    <app-feature-flag-tabs>
      <sbnb-page-header navTabs class="block sbnbDesktop:hidden"></sbnb-page-header>
    </app-feature-flag-tabs>

    <div *ngIf="isLoading" class="flex items-center justify-center p-8">
      <mat-spinner diameter="40"></mat-spinner>
    </div>

    <ng-container *ngIf="!isLoading">
      <!-- Header with toggle, name, and group picker -->
      <div class="flex items-center justify-between gap-4 mb-6">
        <div class="flex items-center gap-4 flex-grow">
          <mat-slide-toggle
            *ngIf="currentGroup?.settings"
            [checked]="currentGroup.settings.scheduled_autoreply_enabled"
            (change)="toggleAutoReply($event)"></mat-slide-toggle>

          <h2 class="nomargin input__editable flex-grow overflow-hidden" *ngIf="currentGroup">
            <input
              #nameInput
              class="input__borderless w-full"
              type="text"
              [(ngModel)]="currentGroup.name"
              (change)="updateGroupName()"
              placeholder="Enter group name" />
          </h2>

          <!-- Fallback heading when no group is loaded -->
          <h2 *ngIf="!currentGroup">Settings</h2>
        </div>

        <!-- Group Picker -->
        <div class="flex items-center gap-2 flex-shrink-0">
          <mat-form-field appearance="outline" class="w-64">
            <mat-select [value]="currentGroup?.id || ''" (selectionChange)="onGroupChange($event)">
              <mat-option *ngFor="let group of settingsGroups$ | async" [value]="group?.id">
                {{ group?.name || 'Unnamed Group' }}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>
      </div>

      <!-- Only render settings section when currentGroup.settings is defined -->
      <ng-container *ngIf="currentGroup?.settings">
        <!-- Settings Content with Tabs -->
        <mat-tab-group animationDuration="0ms" class="mb-6 tabs-left-aligned" [(selectedIndex)]="selectedTabIndex">
          <!-- Main Settings Tab -->
          <mat-tab label="Settings">
            <div class="pt-4">
              <div class="flex flex-col gap-10">
                <!-- Auto Reply Mode -->
                <div class="flex flex-col gap-2">
                  <strong>Auto Reply Mode</strong>
                  <div class="flex flex-col gap-2">
                    <mat-radio-group
                      [(ngModel)]="currentGroup.settings.inbox_ai_mode"
                      (ngModelChange)="patchSetting('inbox_ai_mode', currentGroup.settings.inbox_ai_mode)"
                      class="flex flex-col gap-2"
                      aria-label="Auto Reply Mode"
                    >
                      <mat-radio-button
                        [value]="InboxAIAutoreplyMode.Send"
                        class="flex items-center gap-2"
                      >
                        <span>Send immediately</span>
                        <!-- <span class="text-xs text-night-lighter ml-2">AI messages will be sent without delay.</span> -->
                      </mat-radio-button>
                      <mat-radio-button
                        [value]="InboxAIAutoreplyMode.Delay"
                        class="flex items-center gap-2"
                      >
                        <span>Delay sending</span>
                        <!-- <span class="text-xs text-night-lighter ml-2">AI messages will be drafted and held for a specified time before sending.</span> -->
                      </mat-radio-button>
                    </mat-radio-group>
                    @if (currentGroup.settings.inbox_ai_mode === InboxAIAutoreplyMode.Delay) {
                      <div class="flex items-center gap-2 mt-2">
                        <span>Prepare and delay for</span>
                        <div class="inline-flex">
                          <input
                            type="number"
                            min="1"
                            max="120"
                            [(ngModel)]="currentGroup.settings.inbox_ai_delay_minutes"
                            (blur)="patchSetting('inbox_ai_delay_minutes', currentGroup.settings.inbox_ai_delay_minutes)"
                            data-element="inbox-ai-delay-minutes"
                            class="w-16 h-10 px-3 py-2 border border-gray-300 rounded-md text-center focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                          />
                        </div>
                        <span>minutes.</span>
                      </div>
                      <span class="text-sm text-night-lighter mt-2">
                        AI messages will be drafted and held for a specified time before sending. You will be able to edit or cancel them in the guest conversation.
                      </span>
                    }
                  </div>
                </div>

                <!-- Escalation Policy -->
                <div class="flex flex-col gap-2">
                  <strong>Escalation policy</strong>
                  <span class="text-sm text-night-lighter">
                    Define when conversations should be escalated to you rather than continue to be handled by AI.
                  </span>
                  <div class="textarea-container">
                    <textarea
                      matInput
                      cdkTextareaAutosize
                      cdkAutosizeMinRows="2"
                      data-element="inbox-ai-escalation-policy"
                      [(ngModel)]="currentGroup.settings.inbox_ai_escalation_policy"
                      (blur)="patchSetting('inbox_ai_escalation_policy', currentGroup.settings.inbox_ai_escalation_policy)"
                      placeholder="Enter your escalation policy"></textarea>
                  </div>
                </div>

                <!-- Escalation Message -->
                <div class="flex flex-col gap-2">
                  <strong>Escalation message</strong>
                  <span class="text-sm text-night-lighter">
                    This exact message will be sent to guests when the AI escalates a conversation.
                  </span>
                  <div class="textarea-container">
                    <textarea
                      matInput
                      cdkTextareaAutosize
                      cdkAutosizeMinRows="2"
                      data-element="inbox-ai-escalation-message"
                      [(ngModel)]="currentGroup.settings.inbox_ai_escalation_message"
                      (blur)="patchSetting('inbox_ai_escalation_message', currentGroup.settings.inbox_ai_escalation_message)"
                      [placeholder]="defaultSettings.inbox_ai_escalation_message"></textarea>
                  </div>
                </div>

                <!-- AI Attribution -->
                <div class="flex flex-col gap-2">
                  <div fxLayout fxLayoutAlign="start center" fxLayoutGap="12px">
                    <mat-slide-toggle
                      [(ngModel)]="currentGroup.settings.inbox_ai_attribution_enabled"
                      (ngModelChange)="patchSetting('inbox_ai_attribution_enabled', currentGroup.settings.inbox_ai_attribution_enabled)"
                      data-element="inbox-ai-attribution-toggle">
                      <strong>Enable AI attribution</strong>
                    </mat-slide-toggle>
                  </div>
                  <span class="text-sm text-night-lighter">
                    When enabled, this message will be added to AI-generated responses to inform guests they are talking to an
                    AI.
                  </span>

                  <div *ngIf="currentGroup.settings.inbox_ai_attribution_enabled" class="flex flex-col gap-2 mt-2">
                    <div class="textarea-container">
                      <textarea
                        matInput
                        cdkTextareaAutosize
                        cdkAutosizeMinRows="2"
                        data-element="inbox-ai-attribution-message"
                        [(ngModel)]="currentGroup.settings.inbox_ai_attribution_message"
                        (blur)="patchSetting('inbox_ai_attribution_message', currentGroup.settings.inbox_ai_attribution_message)"
                        placeholder="Enter your AI attribution message"></textarea>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </mat-tab>

          <!-- Timing Tab -->
          <mat-tab label="Timing">
            <div class="pt-4">
              <div class="flex flex-col gap-10">
                <div class="flex flex-col gap-2">
                  <div fxLayout fxLayoutAlign="start center" fxLayoutGap="12px">
                    <strong>Schedule</strong>
                  </div>
                  <span class="text-sm text-night-lighter">
                    When switched on, Hospitable's AI will automatically respond to guests between the selected times (in your
                    property's timezone).
                  </span>

                  <div class="flex flex-col gap-4 mt-2">
                    <div class="flex items-center gap-4">
                      <span>From</span>
                      <sbnb-time-picker
                        data-element="inbox-ai-start-time"
                        [hours]="getHours(currentGroup.settings.scheduled_autoreply_start_time)"
                        [minutes]="getMinutes(currentGroup.settings.scheduled_autoreply_start_time)"
                        (timeUpdated)="updateStartTime($event)"></sbnb-time-picker>
                      <span>to</span>
                      <sbnb-time-picker
                        data-element="inbox-ai-end-time"
                        [hours]="getHours(currentGroup.settings.scheduled_autoreply_end_time)"
                        [minutes]="getMinutes(currentGroup.settings.scheduled_autoreply_end_time)"
                        (timeUpdated)="updateEndTime($event)"></sbnb-time-picker>
                    </div>
                  </div>
                </div>

                <!-- Days of Week -->
                <div class="flex flex-col gap-2">
                  <strong>Days of Week</strong>
                  <span class="text-sm text-night-lighter">
                    Select which days of the week AI autoreply will be active.
                  </span>
                  <div class="flex flex-wrap gap-2 mt-2">
                    <ng-container *ngIf="showEveryDay">
                      <mat-checkbox
                        [checked]="true"
                        (change)="toggleEveryDay($event.checked)"
                        [attr.data-testid]="'inbox-ai-every-day'">
                        Every day of the week
                      </mat-checkbox>
                    </ng-container>
                    <ng-container *ngIf="!showEveryDay">
                      <mat-checkbox
                        *ngFor="let day of allDays"
                        [checked]="currentGroup.settings.scheduled_autoreply_days_of_week?.includes(day)"
                        (change)="toggleDay(day, $event.checked)"
                        [attr.data-testid]="'inbox-ai-day-' + day">
                        {{day | titlecase}}
                      </mat-checkbox>
                    </ng-container>
                  </div>
                </div>

                <!-- Booking Stage -->
                <div class="flex flex-col gap-2">
                  <strong>Booking Stage</strong>
                  <span class="text-sm text-night-lighter">
                    Choose when Auto Reply will attempt to send messages. Deselect stages where you don't want Auto Reply to act at all.
                  </span>
                  <div class="flex flex-col gap-4 mt-2">
                    <div class="flex items-center gap-4">
                      <mat-checkbox
                        [checked]="currentGroup.settings.inbox_ai_thread_stages.includes('prebooking')"
                        (change)="toggleThreadStage('prebooking', $event.checked)"
                        data-element="inbox-ai-stage-prebooking">
                        Inquiries
                      </mat-checkbox>
                      <mat-checkbox
                        [checked]="currentGroup.settings.inbox_ai_thread_stages.includes('precheckin')"
                        (change)="toggleThreadStage('precheckin', $event.checked)"
                        data-element="inbox-ai-stage-precheckin">
                        Before check-in
                      </mat-checkbox>
                      <mat-checkbox
                        [checked]="currentGroup.settings.inbox_ai_thread_stages.includes('staying')"
                        (change)="toggleThreadStage('staying', $event.checked)"
                        data-element="inbox-ai-stage-staying">
                        Staying
                      </mat-checkbox>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </mat-tab>

          <!-- Scope Tab -->
          <mat-tab label="Scope">
            <div class="pt-4">
              <div class="flex flex-col gap-2">
                <span class="text-sm text-night-lighter">
                  Limit the usage of this settings group with the options below.
                </span>
                <div class="mt-2">
                  <sbnb-rule-scope [initial]="currentGroup" (completed)="handleScopeChange($event)"></sbnb-rule-scope>
                </div>
              </div>
            </div>
          </mat-tab>
        </mat-tab-group>
      </ng-container>

      <ng-container *ngIf="currentGroup">
        <!-- Action Buttons and Save Indicator -->
        <div class="flex items-center justify-between gap-2 mt-6">
          <div class="flex items-center gap-2">
            <sbnb-button
              label="Delete"
              icon="delete"
              type="text"
              [labelColor]="'#ED3C6A'"
              (clicked)="deleteSettingsGroupPrompt()"></sbnb-button>

            <sbnb-button
              label="Copy"
              icon="copy"
              type="text"
              [labelColor]="'#ED3C6A'"
              (clicked)="copySettingsGroup()"></sbnb-button>
          </div>
          <sbnb-save-indicator [saveStatus]="saveStatus"></sbnb-save-indicator>
        </div>
      </ng-container>

      <!-- Message when no data is available -->
      <div *ngIf="!currentGroup?.settings" class="p-6 text-center">
        <p>No settings group is available. Please create a new group using the dropdown above.</p>
      </div>
    </ng-container>
  </main>
</div>

<sbnb-mobile-sidebar [navTabsHide]="true" [title]="'Guest Experience'">
  <sbnb-gx-subnav></sbnb-gx-subnav>
</sbnb-mobile-sidebar>

